import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import Input from '../Input/Input';
import Select from '../Select/Select';
import Switch from '../Switch/Switch';
import styles from './BulkEditDialog.module.css';

const BulkEditDialog = ({
    show,
    onClose,
    onApply,
    loading = false,
    selectedCount = 0,
    editableFields = [
        { key: 'name', label: 'Название', type: 'text' },
        { key: 'status', label: 'Статус', type: 'select', options: ['active', 'inactive', 'planned'] },
        { key: 'category', label: 'Категория', type: 'select', options: ['commercial', 'residential', 'public'] }
    ]
}) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Сбрасываем ошибку для поля
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};
        editableFields.forEach(field => {
            if (field.required && !formData[field.key]) {
                newErrors[field.key] = 'Это поле обязательно';
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleApply = () => {
        if (validate()) {
            if (onApply) {
                onApply(formData);
            }
        }
    };

    const handleClose = () => {
        setFormData({});
        setErrors({});
        onClose();
    };

    return (
        <Modal
            show={show}
            onClose={handleClose}
            title="Массовое редактирование"
            size="md"
            footer={
                <>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleApply}
                        loading={loading}
                        disabled={selectedCount === 0}
                    >
                        Применить к {selectedCount} объектам
                    </Button>
                </>
            }
        >
            <div className={styles.content}>
                {/* Информация о выбранных объектах */}
                <div className={styles.info}>
                    <p>
                        Выбрано объектов: <strong>{selectedCount}</strong>
                    </p>
                    <p className={styles.hint}>
                        Укажите поля, которые нужно изменить. Пустые поля не будут изменены.
                    </p>
                </div>

                {/* Поля для редактирования */}
                <div className={styles.fields}>
                    {editableFields.map(field => (
                        <div key={field.key} className={styles.field}>
                            <label className={styles.label}>{field.label}</label>

                            {field.type === 'text' && (
                                <Input
                                    value={formData[field.key] || ''}
                                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                    placeholder={`Введите ${field.label.toLowerCase()}`}
                                    error={errors[field.key]}
                                />
                            )}

                            {field.type === 'select' && (
                                <Select
                                    options={field.options.map(opt => ({
                                        value: opt,
                                        label: opt
                                    }))}
                                    value={formData[field.key] || ''}
                                    onChange={(val) => handleFieldChange(field.key, val)}
                                    placeholder={`Выберите ${field.label.toLowerCase()}`}
                                />
                            )}

                            {field.type === 'boolean' && (
                                <Switch
                                    checked={formData[field.key] || false}
                                    onChange={(val) => handleFieldChange(field.key, val)}
                                    label={field.label}
                                />
                            )}

                            {field.type === 'number' && (
                                <Input
                                    type="number"
                                    value={formData[field.key] || ''}
                                    onChange={(e) => handleFieldChange(field.key, parseFloat(e.target.value) || 0)}
                                    placeholder={`Введите ${field.label.toLowerCase()}`}
                                    error={errors[field.key]}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* Предпросмотр изменений */}
                {Object.keys(formData).length > 0 && (
                    <div className={styles.preview}>
                        <h4 className={styles.previewTitle}>Будут изменены:</h4>
                        <ul className={styles.previewList}>
                            {Object.entries(formData)
                                .filter(([_, value]) => value !== '' && value != null)
                                .map(([key, value]) => {
                                    const field = editableFields.find(f => f.key === key);
                                    return (
                                        <li key={key} className={styles.previewItem}>
                                            <span className={styles.previewField}>{field?.label || key}:</span>
                                            <span className={styles.previewValue}>{String(value)}</span>
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default BulkEditDialog;
